import { ComponentList } from '../../components/core/component-list.js';
import { MapUtils } from '../../utility/map-utils.js';
import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModel,
  DataModelBuilder, isSameData, isSameList, SerializationContext,
  SerializedData,
} from '../dto/dto.js';
import { LocalizedRichDescription } from './localized-rich-description.js';
import { LocalizedString } from './localized-string.js';
import { Price } from './price.js';
import {
  fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString,
  toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { Language } from './language.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { LegacyOrderHook, LegacyOrderHookId } from './legacy-order-hook.js';
import { VersionRange } from '../core/version-range.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { InvoiceVat } from './invoicing/invoice-vat.js';
import { EntityMixin } from '../../components/core/entity-mixin.js';
import { OrderSideEffect } from './orders/order-side-effect.js';
import { OrderSideEffectType } from './orders/order-side-effect-type.js';
import { toSafeFilename } from '../../utility/string.js';
import { Dayjs } from 'dayjs';
import { TrueTime } from '../../time/true-time.js';

export enum ProductStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export enum ProductType {
  Generic = 'Generic',
  Book = 'Book',
  GiftCard = 'GiftCard',
  OpenAccessPass = 'OpenAccessPass',
  OpenTicketPass = 'OpenTicketPass',
}

export class Product extends EntityMixin(DataModel) {
  static readonly TYPE_ID: string = 'ed4387cc3983460f81bc55cd886f0276';
  static readonly SCHEMA_VERSION: number = 8;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: Dayjs,
    public readonly names: LocalizedString,
    public readonly richDescriptions: LocalizedRichDescription,
    public readonly coverImage: PhotoReference | null,

    // Selling price of the product.
    public readonly price: Price,

    // If there is an offer on the product this would be the original price before the offer was applied for the final
    // selling price. When the user applies a discount code, this is the selling price before the discount is applied.
    public readonly originalPrice: Price | null,

    public readonly isPurchasable: boolean,
    public readonly status: ProductStatus,
    public readonly components: ComponentList,
    public readonly sideEffects: OrderSideEffect[],
    public readonly overrideVat: InvoiceVat | null,
    public readonly type: ProductType,
    public readonly assignable: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return Product.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(6, Product.SCHEMA_VERSION);
  }

  get hasDiscount(): boolean {
    return isNotNullOrUndefined(this.originalPrice) && this.originalPrice.value > this.price.value;
  }

  get discountPercentage(): number {
    return isNotNullOrUndefined(this.originalPrice) ? 100.0 - (this.price.value * 100.0 / this.originalPrice.value) : 0;
  }

  static create(data: {
    uniqueId: string,
    businessRef: BusinessModuleRef,
    creationDate: Dayjs,
    names: LocalizedString,
    richDescriptions: LocalizedRichDescription,
    coverImage: PhotoReference | null,
    price: Price,
    originalPrice: Price | null,
    isPurchasable: boolean,
    status: ProductStatus,
    components: ComponentList,
    sideEffects: OrderSideEffect[],
    overrideVat: InvoiceVat | null,
    type: ProductType,
    assignable?: boolean,
  }) {
    return new Product(
        data.uniqueId,
        data.businessRef,
        data.creationDate,
        data.names,
        data.richDescriptions,
        data.coverImage,
        data.price,
        data.originalPrice,
        data.isPurchasable,
        data.status,
        data.components,
        data.sideEffects,
        data.overrideVat,
        data.type,
        data.assignable ?? false,
    );
  }

  static basic(data: {
    businessRef: BusinessModuleRef,
    uniqueId: string,
    names: Map<Language, string>,
    price: Price,
    coverImage: PhotoReference | null,
    type: ProductType,
    assignable?: boolean,
    isPurchasable?: boolean,
    components?: ComponentList,
    sideEffects?: OrderSideEffect[],
    creationDate?: Dayjs,
  }): Product {
    return this.create({
      uniqueId: data.uniqueId,
      businessRef: data.businessRef,
      creationDate: data.creationDate ?? TrueTime.now(),
      names: LocalizedString.localized(data.names),
      richDescriptions: LocalizedRichDescription.empty(),
      coverImage: data.coverImage,
      price: data.price,
      originalPrice: null,
      isPurchasable: data.isPurchasable ?? true,
      status: ProductStatus.Live,
      components: data.components ?? new ComponentList(),
      sideEffects: data.sideEffects ?? [],
      overrideVat: null,
      type: data.type,
      assignable: data.assignable ?? false,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): Product {
    return this.create({
      uniqueId: MapUtils.get<string>(map, 'uniqueId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      names: context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
      richDescriptions: context.fromData<LocalizedRichDescription>(MapUtils.get<SerializedData>(map, 'richDescriptions')),
      coverImage: context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'coverImage')),
      price: Price.decode(MapUtils.get<string>(map, 'price')),
      originalPrice: Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'originalPrice')),
      isPurchasable: MapUtils.get<boolean>(map, 'isPurchasable'),
      status: toEnumFromString(MapUtils.get<string>(map, '$status'), ProductStatus),
      components: ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      ),
      sideEffects: MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => {
        return context.fromData<OrderSideEffect>(value as SerializedData);
      }),
      overrideVat: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'overrideVat'), InvoiceVat),
      type: toEnumFromString(MapUtils.getString(map, 'type'), ProductType),
      assignable: MapUtils.getBoolean(map, 'assignable'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'names': context.toData(this.names),
      'richDescriptions': context.toData(this.richDescriptions),
      'coverImage': context.toDataOrNull(this.coverImage),
      'price': this.price.encode(),
      'originalPrice': this.originalPrice?.encode() ?? null,
      'isPurchasable': this.isPurchasable,
      '$status': fromEnumAsString(this.status),
      'components': this.components.serialize(context),
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'overrideVat': fromEnumAsStringOrNull(this.overrideVat),
      'type': fromEnumAsString(this.type),
      'assignable': this.assignable,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameData(other.businessRef, this.businessRef) &&
      isSameData(other.names, this.names) &&
      other.creationDate === this.creationDate &&
      isSameData(other.richDescriptions, this.richDescriptions) &&
      isSameData(other.coverImage, this.coverImage) &&
      isSameData(other.price, this.price) &&
      isSameData(other.originalPrice, this.originalPrice) &&
      isSameList(other.sideEffects, this.sideEffects, isSameData) &&
      other.isPurchasable === this.isPurchasable &&
      other.status === this.status &&
      isSameData(other.components, this.components) &&
      other.overrideVat === this.overrideVat &&
      other.type === this.type &&
      other.assignable === this.assignable;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return this.copyWith({
      businessRef,
      components: this.components.copyWithBusinessRef(businessRef),
    });
  }

  copyWith({
    uniqueId = this.uniqueId,
    businessRef = cloneData(this.businessRef),
    creationDate = this.creationDate,
    names = cloneData(this.names),
    richDescriptions = cloneData(this.richDescriptions),
    coverImage = cloneDataOrNull(this.coverImage),
    price = cloneData(this.price),
    originalPrice = cloneDataOrNull(this.originalPrice),
    isPurchasable = this.isPurchasable,
    status = this.status,
    components = cloneData(this.components),
    sideEffects = cloneListOfCloneable(this.sideEffects),
    overrideVat = this.overrideVat,
    type = this.type,
    assignable = this.assignable,
  }: {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    names?: LocalizedString,
    creationDate?: Dayjs,
    richDescriptions?: LocalizedRichDescription,
    coverImage?: PhotoReference | null,
    price?: Price,
    originalPrice?: Price | null,
    isPurchasable?: boolean,
    status?: ProductStatus,
    components?: ComponentList,
    sideEffects?: OrderSideEffect[],
    overrideVat?: InvoiceVat | null,
    type?: ProductType,
    assignable?: boolean,
  } = {}): this {
    return Product.create({
      uniqueId,
      businessRef,
      names,
      creationDate,
      richDescriptions,
      coverImage,
      price,
      originalPrice,
      isPurchasable,
      status,
      components,
      sideEffects,
      overrideVat,
      type,
      assignable,
    }) as this;
  }

  findSideEffect(effectType: OrderSideEffectType): OrderSideEffect | null {
    for(let i = 0; i < this.sideEffects.length; i++) {
      if(this.sideEffects[i].effectType === effectType) {
        return this.sideEffects[i];
      }
    }

    return null;
  }

  hasSideEffect(effectType: OrderSideEffectType): boolean {
    return this.findSideEffect(effectType) != null;
  }

  /**
     * Generates the storage path for products.
     * @param {string} customerId The customer ID.
     * @param {string} orderId The order ID.
     * @param {string} productId The product template ID.
     * @param {string} productInstanceId The product instance ID.
     * @param {string | null} eventName The event name.
     * @return {string} The storage path.
     */
  static generateCustomerStoragePath({ customerId, orderId, productId, productInstanceId, eventName }: {
        customerId: string,
        orderId: string,
        productId: string,
        productInstanceId: string,
        eventName: string | null,
    }): string {
    const sanitizedEventName = eventName != null ? toSafeFilename(eventName).substring(0, 30) : null;
    let filename = `${productId}_${productInstanceId}`;
    if(sanitizedEventName != null) {
      filename = `${sanitizedEventName}_${filename}`;
    }

    return `user_accounts/${customerId}/orders/${orderId}/products/${filename}.pdf`;
  }

  /**
     * Generates the storage path for products in a guest order.
     * @param {string} orderId The order ID.
     * @param {string} businessId The business ID.
     * @param {string} productId The product template ID.
     * @param {string} productInstanceId The product instance ID.
     * @param {string | null} eventName The event name.
     * @return {string} The storage path.
     */
  static generateGuestStoragePath({ orderId, productId, productInstanceId, businessId, eventName }: {
        orderId: string,
        productId: string,
        productInstanceId: string,
        businessId: string,
        eventName: string | null,
    }): string {
    const sanitizedEventName = eventName != null ? toSafeFilename(eventName).substring(0, 30) : null;
    let filename = `${productId}_${productInstanceId}`;
    if(sanitizedEventName != null) {
      filename = `${sanitizedEventName}_${filename}`;
    }

    return `business_profiles/${businessId}/orders/${orderId}/products/${filename}.pdf`;
  }
}

export class ProductBuilder extends DataModelBuilder<Product> {
  build(context: SerializationContext, map: SerializedData): Product {
    return Product.fromMap(context, map);
  }

  onBeforeDeserialization(context: SerializationContext, map: SerializedData): void {
    const price = Price.decode(MapUtils.get<string>(map, 'price'));
    context.storage.set('productPrice', price);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    this.migrateEnum(map, '$status', ProductStatus, ProductStatus.Live);

    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(Product.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(Product.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version parameter `onOrderCompleteHooks` of type `OrderHook[]` was introduced.
    if(version < 2) {
      map['onOrderCompleteHooks'] = [];
      version = 2;
    }

    // In this version parameter `coverImage` of type `PhotoReference | null` was introduced.
    if(version < 3) {
      version = 3;
    }

    // Version 4:
    // - Add `overrideVat` of type `InvoiceVat | null`.
    if(version < 4) {
      version = 4;
    }

    // Version 5:
    //  - parameter `type` of type `ProductType` was introduced
    if(version < 5) {
      const hooks = MapUtils.getList<LegacyOrderHook>(map, 'onOrderCompleteHooks', (value) => {
        return context.fromData<LegacyOrderHook>(value as SerializedData);
      });

      let type : ProductType | null = null;
      if(versionRange.to < 2) {
        type = ProductType.OpenTicketPass;
      }
      else {
        for(const hook of hooks) {
          switch(hook.uniqueId) {
            case LegacyOrderHookId.CreateGiftCard: {
              type = ProductType.GiftCard;
              break;
            }
            case LegacyOrderHookId.CreateSeasonPass:
            case LegacyOrderHookId.CreateOpenPass:
            case LegacyOrderHookId.CreateOpenTicketPass: {
              type = ProductType.OpenTicketPass;
              break;
            }
            case LegacyOrderHookId.CreateOpenAccessPass: {
              type = ProductType.OpenAccessPass;
              break;
            }
            case LegacyOrderHookId.CreateOpenAccessPassPlaceholder: {
              type = ProductType.OpenAccessPass;
              break;
            }
            case LegacyOrderHookId.CreateOpenAccessPassFromTicket: {
              type = ProductType.OpenAccessPass;
              break;
            }
          }
        }

        type ??= ProductType.Book;
      }

      map['type'] = fromEnumAsString(type);
      version = 5;
    }

    if(version < 6) {
      const onOrderCompleteHooks = MapUtils.getList<LegacyOrderHook>(map, 'onOrderCompleteHooks', (value) => {
        return context.fromData<LegacyOrderHook>(value as SerializedData);
      });

      const sideEffects = onOrderCompleteHooks.map((e) => e.toSideEffect()).filter(isNotNullOrUndefined);

      map['sideEffects'] = sideEffects.map((e) => context.toData(e));
      version = 6;
    }

    /**
     * Version 7:
     * - add `creationDate` of type `Dayjs`.
     */
    if(version < 7) {
      MapUtils.setIfNullOrUndefined(
          map, 'creationDate',
          MapUtils.getRaw(map, '$last_update') ?? context.encodeDate(TrueTime.now()),
      );

      version = 7;
    }

    /**
     * Version 8:
     * - add `assignable` of type `boolean`.
     */
    if(version < 8) {
      MapUtils.setIfNullOrUndefined(map, 'assignable', true);
      version = 8;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
